import { Modal, Form, notification, Switch, Button, Space } from 'antd';
import { useIntl } from 'react-intl';

import {
  CreatePlanAccountRequest,
  UpdatePlanAccountRequest,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import {
  useActivateOrInactivatePlanAccount,
  useCreatePlanAccount,
  usePlanAccount,
  useUpdatePlanAccount,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.hooks';

import AccountsPlanAccountsGeneralForm from '../Form';
import { useEffect, useState } from 'react';
import { SaveButton } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';

interface AccountsPlanAccountsGeneralModalFormProps {
  planAccountId?: number;
  visible: boolean;
  onCancel: () => void;
}

function AccountsPlanAccountsGeneralModalForm({
  planAccountId,
  visible,
  onCancel,
}: AccountsPlanAccountsGeneralModalFormProps) {
  const intl = useIntl();

  const [form] = Form.useForm<CreatePlanAccountRequest & { subcode: string }>();
  const [active, setActive] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const { data: planAccountData, mutateAsync } = usePlanAccount();

  const createMutation = useCreatePlanAccount();
  const updateMutation = useUpdatePlanAccount();

  const activateOrInactivatePlanAccount = useActivateOrInactivatePlanAccount();

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivatePlanAccount.mutateAsync(Number(planAccountId));

      setActive(active);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  useEffect(() => {
    if (planAccountId) {
      mutateAsync(planAccountId);
    }
  }, [planAccountId]);

  useEffect(() => {
    if (planAccountData) {
      const { code, ...values } = planAccountData;
      form.setFieldsValue({
        ...values,
        code: code.split('.')[0],
        subcode: code.split('.')[1],
      });
      setActive(planAccountData.active);
      setEditMode(true);
    }
  }, [planAccountData]);

  const handleCreate = async () => {
    try {
      const { code, subcode, ...values } = await form.validateFields();
      const checkSyntetic = subcode === '0000' ? '' : '.' + subcode;

      const obj: CreatePlanAccountRequest = {
        code: code + checkSyntetic,
        ...values,
        active: true,
      };

      await createMutation.mutateAsync(obj);

      onCancel();
    } catch {}
  };

  const handleUpdate = async () => {
    try {
      if (planAccountId) {
        const { code, subcode, ...values } = await form.validateFields();
        const checkSyntetic =
          subcode === '0000' || subcode === undefined ? '' : '.' + subcode;

        const obj: UpdatePlanAccountRequest = {
          ...values,
          id: planAccountId,
          code: code + checkSyntetic,
          active,
        };

        await updateMutation.mutateAsync(obj);

        onCancel();
        setEditMode(false);
      }
    } catch {}
  };

  return (
    <Modal
      destroyOnClose
      width={680}
      open={visible}
      confirmLoading={createMutation.isLoading || updateMutation.isLoading}
      onCancel={() => {
        onCancel();
        setEditMode(false);
      }}
      footer={[
        <SpaceForButton justifyContent="end">
          <Space align="center">
            {planAccountId && (
              <>
                <span>
                  {intl.formatMessage({
                    id: 'commons.modal.contact.form.contactList.status',
                  })}
                  :
                </span>

                <Switch
                  unCheckedChildren={intl.formatMessage({
                    id: 'commons.entity.inactive',
                  })}
                  checkedChildren={intl.formatMessage({
                    id: 'commons.entity.active',
                  })}
                  loading={false}
                  checked={active}
                  onClick={handleActiveClick}
                />
              </>
            )}
          </Space>

          <Button
            block
            onClick={() => {
              onCancel();
              setEditMode(false);
            }}
          >
            {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
          </Button>

          <SaveButton
            block
            onClick={planAccountId ? handleUpdate : handleCreate}
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </SaveButton>
        </SpaceForButton>,
      ]}
      title={
        planAccountId
          ? intl.formatMessage({ id: 'planAccounts.modal.general.edit.title' })
          : intl.formatMessage({ id: 'planAccounts.modal.general.new.title' })
      }
    >
      <AccountsPlanAccountsGeneralForm form={form} editMode={editMode} />
    </Modal>
  );
}

export default AccountsPlanAccountsGeneralModalForm;
